// parseLocationInput
// ==================
//
// ULTRA: Parst eine User-Eingabe im Router (Ziel/Start) zu Koordinaten.
// Unterstützt:
//   1. Rohe Koordinaten (lat zuerst, lon zweitens):
//        "43.6108, 3.8767" | "43.6108,3.8767" | "43.6108 3.8767"
//        Deutsche Dezimal-Komma-Form: "43,6108 3,8767" | "43,6108, 3,8767"
//   2. Google-Maps-Langlinks (client-seitig, kein Server nötig):
//        …/@LAT,LON,ZOOMz…            (Karten-Zentrum / place)
//        …!3dLAT!4dLON…              (place-Marker im data=-Teil)
//        …?q=LAT,LON | &q=LAT,LON    (query)
//        …?ll=LAT,LON | &query=LAT,LON
//   3. Gekürzte Google-Maps-Links (maps.app.goo.gl / goo.gl/maps) → müssen
//      server-seitig aufgelöst werden (Redirect folgen) → kind:'shortener'.
//
// NICHT unterstützt (v1): DMS-Grad-Format wie 43°36'38"N 3°52'36"E.

export type ParsedLocation =
  | { kind: 'coords'; lat: number; lon: number }
  | { kind: 'url'; lat: number; lon: number }
  | { kind: 'shortener'; url: string }
  | null;

function validLat(x: number): boolean {
  return Number.isFinite(x) && x >= -90 && x <= 90;
}
function validLon(x: number): boolean {
  return Number.isFinite(x) && x >= -180 && x <= 180;
}

/** Extrahiert lat/lon aus einem (langen) Google-Maps-URL-String. */
export function parseGoogleMapsUrl(
  s: string,
): { lat: number; lon: number } | null {
  // Reihenfolge nach Präzision: !3d!4d (echter place-Marker) > @ (Viewport) >
  // q/ll/query. Alle Google-URLs nutzen Punkt-Dezimale.
  const tries: Array<RegExp> = [
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
    /@(-?\d+\.\d+),(-?\d+\.\d+)/,
    /[?&](?:q|ll|query|destination)=(-?\d+\.\d+),\s*(-?\d+\.\d+)/,
    /[?&](?:q|ll|query|destination)=loc:(-?\d+\.\d+),\s*(-?\d+\.\d+)/,
  ];
  for (const re of tries) {
    const m = s.match(re);
    if (m && m[1] != null && m[2] != null) {
      const lat = parseFloat(m[1]);
      const lon = parseFloat(m[2]);
      if (validLat(lat) && validLon(lon)) return { lat, lon };
    }
  }
  return null;
}

/** Rohe Koordinaten-Eingabe (lat zuerst). Gibt null zurück wenn nicht sicher. */
export function parseRawCoords(
  input: string,
): { lat: number; lon: number } | null {
  const s = input.trim();
  if (!s) return null;
  const patterns: RegExp[] = [
    // Punkt-Dezimale, Trenner Komma(+space) oder space:
    //   43.6108, 3.8767 | 43.6108,3.8767
    /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/,
    //   43.6108 3.8767
    /^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)$/,
    // Deutsche Komma-Dezimale, Trenner Komma+space: 43,6108, 3,8767
    /^(-?\d+,\d+)\s*,\s+(-?\d+,\d+)$/,
    // Deutsche Komma-Dezimale, Trenner space: 43,6108 3,8767
    /^(-?\d+,\d+)\s+(-?\d+,\d+)$/,
  ];
  for (const re of patterns) {
    const m = s.match(re);
    if (!m || m[1] == null || m[2] == null) continue;
    const lat = parseFloat(m[1].replace(',', '.'));
    const lon = parseFloat(m[2].replace(',', '.'));
    if (validLat(lat) && validLon(lon)) return { lat, lon };
  }
  return null;
}

/** True wenn der String ein gekürzter Google-Maps-Link ist (Server-Resolve). */
export function isMapsShortener(s: string): boolean {
  return /https?:\/\/(?:maps\.app\.goo\.gl|goo\.gl\/maps)\//i.test(s.trim());
}

/**
 * Haupt-Parser. Reihenfolge:
 *   1. Shortener-Link → kind:'shortener' (Client ruft Backend).
 *   2. Google-Maps-Langlink → kind:'url'.
 *   3. Rohe Koordinaten → kind:'coords'.
 */
export function parseLocationInput(input: string): ParsedLocation {
  const s = (input || '').trim();
  if (!s) return null;

  if (isMapsShortener(s)) return { kind: 'shortener', url: s };

  const looksLikeUrl = /https?:\/\//i.test(s) || /google\.[a-z.]+\/maps/i.test(s);
  if (looksLikeUrl) {
    const g = parseGoogleMapsUrl(s);
    if (g) return { kind: 'url', lat: g.lat, lon: g.lon };
    return null; // URL, aber keine Koordinaten drin
  }

  const c = parseRawCoords(s);
  if (c) return { kind: 'coords', lat: c.lat, lon: c.lon };
  return null;
}
